// Convex + Clerk React Integration Provider
// This component binds the database backend (Convex) and authentication layer (Clerk) together.
// It wraps the entire application, enabling reactive backend queries that are fully secure.

import React, { ReactNode, useRef, useEffect } from "react";
import { ClerkProvider, useAuth } from "@clerk/clerk-react";
import { ConvexProviderWithClerk } from "convex/react-clerk";
import { ConvexReactClient, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";

// Pull environment variables configured via .env files.
const convexUrl = import.meta.env.VITE_CONVEX_URL;
const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

// Fail-fast checks to prevent starting the app in a broken state due to missing configuration.
if (!convexUrl) {
  throw new Error("Critical Error: VITE_CONVEX_URL environment variable is missing or empty.");
}
if (!clerkPublishableKey) {
  throw new Error("Critical Error: VITE_CLERK_PUBLISHABLE_KEY environment variable is missing or empty.");
}

// Instantiate the singleton Convex Client using the configured endpoint URL.
const convexClient = new ConvexReactClient(convexUrl);

interface ConvexClerkProviderProps {
  children: ReactNode;
}

/**
 * Headless AuthSync Component
 *
 * This component runs inside the ConvexProviderWithClerk context. It monitors the Clerk sign-in state
 * and automatically triggers user record creation (createOrUpdateUser) and free trial activation (startTrial)
 * on the Convex backend exactly once per Clerk session / user ID.
 *
 * We use a useRef key to store the authenticated userId that has been processed. This guarantees
 * that we do not make duplicate mutation calls during React re-renders or session transitions.
 */
const AuthSync: React.FC = () => {
  const { userId, isSignedIn } = useAuth();
  const createOrUpdateUser = useMutation(api.users.createOrUpdateUser);
  const startTrial = useMutation(api.users.startTrial);

  // A ref to store the userId for which provisioning has already run.
  // Using this ensures that user provisioning runs exactly-once per session/login.
  const provisionedUserRef = useRef<string | null>(null);

  useEffect(() => {
    // If signed in, we have a userId, and we haven't provisioned this userId yet:
    if (isSignedIn && userId && provisionedUserRef.current !== userId) {
      // Set the ref immediately to prevent subsequent renders from starting duplicate provisioning requests.
      provisionedUserRef.current = userId;

      // Run provisioning flow:
      // 1. Create or update the user record in Convex.
      // 2. Start the 14-day trial for the user.
      const provisionUser = async () => {
        try {
          // Trigger the Convex mutation to create or update the user row
          await createOrUpdateUser();
          
          // Trigger the mutation to start their free trial if not already started
          await startTrial();
        } catch (error) {
          // Log provisioning errors. In a real-world scenario, you might want to retry
          // or report this to an error tracking service.
          console.error("Error during Convex user provisioning:", error);
          // Optional: clear the ref on failure to allow retry on next mount/render
          // provisionedUserRef.current = null;
        }
      };

      provisionUser();
    }
  }, [isSignedIn, userId, createOrUpdateUser, startTrial]);

  // This is a headless component, so it renders nothing.
  return null;
};

// ConvexClerkProvider binds authentication and backend databases.
// - ClerkProvider manages user sign-in tokens, cookies, and login session state.
// - ConvexProviderWithClerk feeds Clerk's JWT tokens into Convex backend actions.
export const ConvexClerkProvider: React.FC<ConvexClerkProviderProps> = ({ children }) => {
  return (
    <ClerkProvider publishableKey={clerkPublishableKey}>
      <ConvexProviderWithClerk client={convexClient} useAuth={useAuth}>
        <AuthSync />
        {children}
      </ConvexProviderWithClerk>
    </ClerkProvider>
  );
};
